import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CreateTenderDialog = ({ open, onOpenChange, onTenderCreated }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Details, 2: Upload
  const [tenderId, setTenderId] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    deadline: ''
  });
  const [file, setFile] = useState(null);

  const handleCreate = async () => {
    if (!formData.title || !formData.client_name || !formData.deadline) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // 1. Create Tender Record
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tenders`, {
        ...formData,
        deadline: new Date(formData.deadline).toISOString()
      });
      
      setTenderId(res.data.id);
      
      // 2. Upload File if selected
      if (file) {
        await uploadFile(res.data.id);
      } else {
        finish(res.data);
      }
    } catch (err) {
      toast.error("Failed to create tender");
      setLoading(false);
    }
  };

  const uploadFile = async (id) => {
    const data = new FormData();
    data.append('file', file);
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tenders/${id}/upload`, data);
      finish({ id, ...formData });
    } catch (err) {
      toast.error("Tender created but file upload failed");
      finish({ id, ...formData }); // Still finish
    }
  };

  const finish = (tender) => {
    setLoading(false);
    toast.success("Tender Created Successfully");
    onTenderCreated(tender);
    handleClose();
  };

  const handleClose = () => {
    setFormData({ title: '', client_name: '', deadline: '' });
    setFile(null);
    setStep(1);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Tender</DialogTitle>
          <DialogDescription>
            Manually add a tender opportunity to your pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Project Title</Label>
            <Input 
              id="title" 
              placeholder="e.g. City Smart Lighting Upgrade" 
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="client">Client / Agency</Label>
              <Input 
                id="client" 
                placeholder="e.g. Dept of Transport" 
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deadline">Deadline</Label>
              <Input 
                id="deadline" 
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({...formData, deadline: e.target.value})}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Tender Document (PDF)</Label>
            <div className="border-2 border-dashed border-input hover:border-primary/50 rounded-lg p-6 transition-colors bg-muted/10 text-center cursor-pointer relative">
              <input 
                type="file" 
                accept=".pdf,.docx"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => setFile(e.target.files[0])}
              />
              <div className="flex flex-col items-center gap-2">
                {file ? (
                  <>
                    <FileText className="h-8 w-8 text-primary" />
                    <p className="text-sm font-medium text-foreground">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Drop file or click to upload</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {loading ? 'Creating...' : 'Create Tender'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTenderDialog;
